import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function IletisimPage() {
  const [formData, setFormData] = useState({
    ad: '',
    email: '',
    telefon: '',
    sirket: '',
    hizmet: '',
    mesaj: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4cl0d2els5gugpjmem0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          ad: '',
          email: '',
          telefon: '',
          sirket: '',
          hizmet: '',
          mesaj: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // SEO Meta Tags
    document.title = 'İletişim | Adana OSGB Mersin Hatay Osmaniye | ASSA İş Sağlığı Güvenliği';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'ASSA OSGB iletişim bilgileri. Adana, Mersin, Hatay, Osmaniye bölgelerinde iş sağlığı ve güvenliği hizmetleri için bizimle iletişime geçin. İş güvenliği uzmanı, işyeri hekimi, OSGB danışmanlığı. Tel: 0850 346 2772');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Adana OSGB iletişim, Mersin OSGB telefon, Hatay OSGB adres, Osmaniye OSGB iletişim, iş sağlığı ve güvenliği iletişim, ASSA OSGB telefon');
    }

    // Canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://www.assaosgb.com.tr/iletisim');
    }

    // Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "İletişim | ASSA OSGB",
      "description": "Adana, Mersin, Hatay, Osmaniye OSGB iletişim bilgileri",
      "url": "https://www.assaosgb.com.tr/iletisim",
      "mainEntity": {
        "@type": "Organization",
        "name": "ASSA OSGB",
        "telephone": "+90-850-346-2772",
        "email": "assa@assaisg.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Reşatbey Mahallesi, 62015 Sokak No:4, ASSA Plaza",
          "addressLocality": "Seyhan",
          "addressRegion": "Adana",
          "postalCode": "01120",
          "addressCountry": "TR"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "36.99703549102141",
          "longitude": "35.326921312280646"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "18:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "09:00",
            "closes": "15:00"
          }
        ],
        "areaServed": ["Adana", "Mersin", "Hatay", "Osmaniye"]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">İletişim</h1>
              <p className="text-xl text-red-100 max-w-3xl mx-auto">
                ASSA İSG ile iletişime geçin. Uzman ekibimiz size en iyi hizmeti sunmak için hazır.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Info & Form Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">İletişim Bilgileri</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-map-pin-line text-red-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Adres</h3>
                      <p className="text-gray-600">
                        Reşatbey Mahallesi, 62015 Sokak No:4<br />
                        ASSA Plaza Seyhan / Adana
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-phone-line text-red-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Telefon</h3>
                      <a href="tel:08503462772" className="text-gray-600 hover:text-red-600">
                        0850 346 2772
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-mail-line text-red-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">E-posta</h3>
                      <a href="mailto:assa@assaisg.com" className="text-gray-600 hover:text-red-600">
                        assa@assaisg.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-time-line text-red-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Çalışma Saatleri</h3>
                      <p className="text-gray-600">
                        Pazartesi - Cuma: 08:00 - 18:00<br />
                        Cumartesi: 09:00 - 15:00
                      </p>
                    </div>
                  </div>
                </div>

                {/* Service Areas */}
                <div className="mt-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Hizmet Verdiğimiz İller</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {['Adana', 'Mersin', 'Hatay', 'Osmaniye'].map((city) => (
                      <div key={city} className="flex items-center space-x-2">
                        <i className="ri-map-pin-fill text-red-600"></i>
                        <span className="text-gray-700">{city}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Contact Buttons */}
                <div className="mt-8 flex space-x-4">
                  <a
                    href="tel:08503462772"
                    className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line"></i>
                    <span>Hemen Ara</span>
                  </a>
                  <a
                    href="mailto:assa@assaisg.com"
                    className="border border-red-600 text-red-600 px-6 py-3 rounded-lg hover:bg-red-50 transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-line"></i>
                    <span>E-posta Gönder</span>
                  </a>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Teklif İste</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="teklif-iste-formu">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="ad" className="block text-sm font-medium text-gray-700 mb-2">
                        Ad Soyad *
                      </label>
                      <input
                        type="text"
                        id="ad"
                        name="ad"
                        value={formData.ad}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                        placeholder="Adınız ve soyadınız"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                        placeholder="ornek@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-2">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        id="telefon"
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                        placeholder="0555 123 45 67"
                      />
                    </div>
                    <div>
                      <label htmlFor="sirket" className="block text-sm font-medium text-gray-700 mb-2">
                        Şirket Adı
                      </label>
                      <input
                        type="text"
                        id="sirket"
                        name="sirket"
                        value={formData.sirket}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                        placeholder="Şirket adınız"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="hizmet" className="block text-sm font-medium text-gray-700 mb-2">
                      İlgilendiğiniz Hizmet *
                    </label>
                    <select
                      id="hizmet"
                      name="hizmet"
                      value={formData.hizmet}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Hizmet seçiniz</option>
                      <option value="OSGB Hizmetleri">OSGB Hizmetleri</option>
                      <option value="İSG Eğitimleri">İSG Eğitimleri</option>
                      <option value="Laboratuvar Hizmetleri">Laboratuvar Hizmetleri</option>
                      <option value="Risk Değerlendirmesi">Risk Değerlendirmesi</option>
                      <option value="Mobil Sağlık Hizmetleri">Mobil Sağlık Hizmetleri</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="mesaj" className="block text-sm font-medium text-gray-700 mb-2">
                      Mesajınız *
                    </label>
                    <textarea
                      id="mesaj"
                      name="mesaj"
                      value={formData.mesaj}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
                      placeholder="Detaylarınızı ve özel gereksinimlerinizi belirtiniz..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.mesaj.length}/500 karakter
                    </p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="ri-check-circle-line text-green-600 text-xl mr-3"></i>
                        <p className="text-green-800">
                          Mesajınız başarıyla gönderildi. En kısa sürede size dönüş yapacağız.
                        </p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
                        <p className="text-red-800">
                          Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyiniz.
                        </p>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Gönderiliyor...
                      </span>
                    ) : (
                      'Teklif İste'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Ofis Konumumuz</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Adana merkezli ofisimizden Adana, Mersin, Hatay ve Osmaniye illerinde hizmet vermekteyiz.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3194.8944!2d35.326921312280646!3d36.99703549102141!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDU5JzQ5LjMiTiAzNcKwMTknMzYuOSJF!5e0!3m2!1str!2str!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="ASSA İSG Ofis Konumu"
              ></iframe>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}
